import {
  Box,
  Center,
  Divider,
  HStack,
  Stack,
  Text,
  chakra,
} from "@chakra-ui/react";
import { OAuthInteractionRO } from "@intentsify/dto";
import { useQuery } from "@tanstack/react-query";
import { AuthFormContainer, AuthFormHeader } from "auth/components";
import { saveOAuthReturnPath } from "auth/oauthReturnPath";
import { LoginScreenDefinition } from "auth/screens/Login/Login.definition";
import { Button } from "components/Button";
import { Loader } from "components/Loader";
import { useEffect, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { useUser } from "store/store.hooks";
import {
  getOAuthDecisionUrl,
  getOAuthInteraction,
} from "./OAuthAuthorize.requests";

type OAuthDecision = "approve" | "deny";

const responseStatus = (error: unknown): unknown =>
  (error as { response?: { statusCode?: unknown } } | undefined)?.response
    ?.statusCode;

const scopeDescription = (scope: string): string => {
  if (scope === "mcp:read") return "Read Intentsify MCP data";
  if (scope === "mcp:write") return "Change Intentsify MCP data";
  if (scope === "offline_access") {
    return "Keep Claude connected for up to 30 days";
  }
  return scope;
};

const registrationDescription = (
  client: OAuthInteractionRO["client"],
): string => {
  if (client.registration === "metadata-document" && client.verifiedDomain) {
    return `Verified app · ${client.verifiedDomain}`;
  }
  if (client.registration === "dynamic") {
    return "Unverified app · Dynamic registration";
  }
  return "Pre-registered app";
};

const OAuthAuthorize = () => {
  const user = useUser();
  const location = useLocation();
  const { interactionId } = useParams<"interactionId">();
  const [submittedDecision, setSubmittedDecision] = useState<OAuthDecision>();

  useEffect(() => {
    if (!user) saveOAuthReturnPath(location.pathname);
  }, [location.pathname, user]);

  const interaction = useQuery({
    queryKey: ["oauthInteraction", interactionId],
    queryFn: () => getOAuthInteraction(String(interactionId)),
    enabled: Boolean(user && interactionId),
    retry: false,
  });

  if (!user) {
    return <Navigate to={LoginScreenDefinition.navigate()} replace />;
  }

  if (!interactionId) throw new Error("OAuth interaction is missing");

  if (interaction.isError) {
    const isIneligible = responseStatus(interaction.error) === 403;

    return (
      <AuthFormContainer>
        <AuthFormHeader
          text={
            isIneligible
              ? "MCP access unavailable"
              : "Authorization request unavailable"
          }
        />
        <Text color="white" textAlign="left">
          {isIneligible
            ? "MCP access is not enabled for your company."
            : "This authorization request has expired or can no longer be completed. Return to your MCP client and start the connection again."}
        </Text>
      </AuthFormContainer>
    );
  }

  if (interaction.isLoading || !interaction.data) {
    return (
      <AuthFormContainer>
        <Center>
          <Loader />
        </Center>
      </AuthFormContainer>
    );
  }

  const details = interaction.data;
  const decisionUrl = getOAuthDecisionUrl(interactionId);
  const isSubmitting = submittedDecision !== undefined;

  return (
    <AuthFormContainer>
      <AuthFormHeader text="Connect an MCP application" />
      <Stack spacing={4} color="white" textAlign="left">
        <Box>
          <Text fontWeight="bold" fontSize="xl">
            {details.client.name}
          </Text>
          <Text fontSize="sm">{registrationDescription(details.client)}</Text>
        </Box>

        <Divider />

        <Stack spacing={2} fontSize="sm">
          <Text>Callback: {details.callbackHostname}</Text>
          <Box>
            <Text fontWeight="semibold">Resource</Text>
            <Text overflowWrap="anywhere">{details.resource}</Text>
          </Box>
          <Box>
            <Text fontWeight="semibold">Requested access</Text>
            {details.scopes.map((scope) => (
              <Text key={scope}>{scopeDescription(scope)}</Text>
            ))}
          </Box>
        </Stack>

        <HStack justify="flex-end" pt={2}>
          <DecisionForm
            action={decisionUrl}
            decision="deny"
            buttonText="Deny"
            isDisabled={isSubmitting}
            isLoading={submittedDecision === "deny"}
            onSubmit={setSubmittedDecision}
            variant="ghost"
          />
          <DecisionForm
            action={decisionUrl}
            decision="approve"
            buttonText={details.consentRequired ? "Allow" : "Continue"}
            isDisabled={isSubmitting}
            isLoading={submittedDecision === "approve"}
            onSubmit={setSubmittedDecision}
            variant="primary-teal"
          />
        </HStack>
      </Stack>
    </AuthFormContainer>
  );
};

const DecisionForm = ({
  action,
  decision,
  buttonText,
  isDisabled,
  isLoading,
  onSubmit,
  variant,
}: {
  action: string;
  decision: OAuthDecision;
  buttonText: string;
  isDisabled: boolean;
  isLoading: boolean;
  onSubmit: (decision: OAuthDecision) => void;
  variant: "ghost" | "primary-teal";
}) => (
  <chakra.form
    action={action}
    method="post"
    onSubmit={() => onSubmit(decision)}
  >
    <input type="hidden" name="decision" value={decision} />
    <Button
      type="submit"
      isDisabled={isDisabled}
      isLoading={isLoading}
      variant={variant}
    >
      {buttonText}
    </Button>
  </chakra.form>
);

export { OAuthAuthorize };
